import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/content";
import { Download, UserMinus } from "lucide-react";

const statusStyle = (s) => {
  switch (s) {
    case "active":
      return "bg-bz-petrol text-white";
    case "pending":
      return "bg-bz-amber/20 text-bz-amber";
    case "unsubscribed":
      return "bg-bz-slate/20 text-bz-slate";
    default:
      return "bg-bz-line text-bz-slate";
  }
};

const statusLabelFr = (s) =>
  ({ active: "Actif", pending: "En attente", unsubscribed: "Désinscrit" }[s] || s);

export default function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/subscribers").then(({ data }) => setSubs(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "abonnes-benzenati.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const unsubscribe = async (id, email) => {
    if (!window.confirm(`Désinscrire « ${email} » ?`)) return;
    await api.post(`/admin/subscribers/${id}/unsubscribe`);
    load();
  };

  const activeCount = subs.filter((s) => s.status === "active").length;

  return (
    <div data-testid="admin-subscribers">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-bz-navy">Abonnés</h1>
          <p className="mt-2 text-sm text-bz-slate">{subs.length} inscription(s) · {activeCount} actif(s)</p>
        </div>
        <button
          onClick={exportCsv}
          disabled={exporting || subs.length === 0}
          data-testid="subscribers-export"
          className="inline-flex items-center gap-2 bg-bz-navy px-5 py-3 text-xs uppercase tracking-[0.15em] text-bz-ivory transition-colors hover:bg-bz-petrol disabled:opacity-60"
        >
          <Download size={16} strokeWidth={1.6} /> {exporting ? "Export…" : "Exporter en CSV"}
        </button>
      </div>

      {loading ? (
        <p className="mt-10 text-bz-slate">Chargement…</p>
      ) : (
        <div className="mt-8 overflow-x-auto border border-bz-line">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="border-b border-bz-line bg-bz-ivory2 text-xs uppercase tracking-[0.1em] text-bz-slate">
              <tr>
                <th className="px-5 py-4">Adresse</th>
                <th className="px-5 py-4">Prénom</th>
                <th className="px-5 py-4">Entreprise</th>
                <th className="px-5 py-4">Fonction</th>
                <th className="px-5 py-4">Statut</th>
                <th className="px-5 py-4">Consentement</th>
                <th className="px-5 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bz-line">
              {subs.map((s) => (
                <tr key={s.id} data-testid={`subscriber-row-${s.id}`} className="bg-white">
                  <td className="px-5 py-4 font-medium text-bz-navy">{s.email}</td>
                  <td className="px-5 py-4 text-bz-slate">{s.first_name || "—"}</td>
                  <td className="px-5 py-4 text-bz-slate">{s.company || "—"}</td>
                  <td className="px-5 py-4 text-bz-slate">{s.role || "—"}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.1em] ${statusStyle(s.status)}`}>{statusLabelFr(s.status)}</span>
                  </td>
                  <td className="px-5 py-4 text-bz-slate">{formatDate(s.consent_date)}</td>
                  <td className="px-5 py-4 text-right">
                    {s.status !== "unsubscribed" && (
                      <button onClick={() => unsubscribe(s.id, s.email)} data-testid={`unsubscribe-${s.id}`} title="Désinscrire" className="inline-flex items-center gap-1.5 text-bz-slate hover:text-red-600">
                        <UserMinus size={16} strokeWidth={1.5} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {subs.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-bz-slate">Aucun abonné pour le moment.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
